// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useQueryClient } from "@tanstack/react-query";
import { Check, CheckCircle, Images, TriangleAlert, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  type ProductImageFileValidation,
  type ProductImagePrecheck,
  useCreateProductImageBatchMutation,
  usePrecheckProductImagesMutation,
  useProductImagesOptionsQuery,
  usePublishProductImageBatchMutation,
  useUploadProductImageFileMutation,
} from "@/api/hooks/use-product-images";
import { InlineSpinner } from "@/components/shared/inline-spinner";
import { KeyChip } from "@/components/shared/key-chip";
import { ProgressBar } from "@/components/shared/progress-bar";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ProductImageValidationBadge } from "@/features/ingress/product-images/product-image-badges";
import {
  ACCEPTED_IMAGE_ACCEPT,
  mappedFileNames,
  mergeSelectedFiles,
  runWithConcurrency,
} from "@/features/ingress/product-images/product-images-shared";
import { cn } from "@/lib/utils";

interface UploadProductImagesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type WizardStep = "scope" | "files" | "preview" | "publish";

type PreviewFilter = ProductImageFileValidation | "all";

const PREVIEW_FILTERS: PreviewFilter[] = ["all", "mapped", "not_in_catalog", "parse_failed", "duplicate"];

// The preview is a plain table; a Leclerc-sized batch has ~20k rows, so only the head is rendered.
const PREVIEW_ROWS_LIMIT = 300;

// Preview order: files that need attention first, mapped files last.
const PREVIEW_ORDER: Record<ProductImageFileValidation, number> = {
  parse_failed: 0,
  not_in_catalog: 1,
  duplicate: 2,
  mapped: 3,
};

// Uploads in flight at once. Each one only lands in the batch's staging folder, so they are cheap.
const UPLOAD_CONCURRENCY = 8;

// Preview thumbnails are rendered this wide, as data URLs, so they need no blob URL lifecycle.
const THUMBNAIL_WIDTH = 80;

/** Downscale an image file to a small JPEG data URL; null when the browser cannot decode it. */
async function makeThumbnail(file: File): Promise<string | null> {
  try {
    const bitmap = await createImageBitmap(file, { resizeWidth: THUMBNAIL_WIDTH, resizeQuality: "low" });
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
    bitmap.close();
    return canvas.toDataURL("image/jpeg", 0.7);
  } catch {
    return null;
  }
}

interface UploadCounts {
  done: number;
  /** Requests that did not reach the backend (network or HTTP failure); the file is missing from the batch. */
  errors: number;
}

const NO_UPLOADS: UploadCounts = { done: 0, errors: 0 };

/** Dashed drop zone for many images at once; nothing leaves the browser until the batch is published. */
function ImageFilesPicker({
  onSelect,
  disabled,
  title,
  subtitle,
}: {
  onSelect: (files: File[]) => void;
  disabled?: boolean;
  title: string;
  subtitle: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  function select(list: FileList | null) {
    if (!list || list.length === 0) return;
    const accepted = ACCEPTED_IMAGE_ACCEPT.split(",");
    onSelect([...list].filter((file) => accepted.some((ext) => file.name.toLowerCase().endsWith(ext))));
  }

  return (
    <div
      className={cn(
        "flex h-[6.5rem] items-center justify-center rounded-lg border-2 border-dashed p-4 text-center transition-colors",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        isDragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-muted-foreground/50",
      )}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (!disabled) select(e.dataTransfer.files);
      }}
      onClick={() => !disabled && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        accept={ACCEPTED_IMAGE_ACCEPT}
        disabled={disabled}
        onChange={(e) => {
          select(e.target.files);
          e.target.value = "";
        }}
      />
      <div>
        <Upload className="mx-auto mb-1 h-6 w-6 text-muted-foreground" />
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}

// Tone of each preview filter's count when it is not zero; "all" stays neutral.
const FILTER_TONES: Record<PreviewFilter, string> = {
  all: "",
  mapped: "text-green-700 dark:text-green-300",
  not_in_catalog: "text-destructive",
  parse_failed: "text-destructive",
  duplicate: "text-amber-700 dark:text-amber-300",
};

/** One preview filter: its name and how many files fall under it; the selected one is filled. */
function FilterChip({
  label,
  value,
  tone,
  selected,
  onClick,
}: {
  label: string;
  value: number;
  tone: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Button size="sm" variant={selected ? "default" : "outline"} onClick={onClick} className="gap-1.5">
      <span>{label}</span>
      <span className={cn("font-medium tabular-nums", !selected && value > 0 && tone)}>{value}</span>
    </Button>
  );
}

/**
 * Bulk product image upload. Scope and file selection stay in the browser: the only server call before
 * the user publishes is the name-only precheck. Publishing creates the batch, streams the mapped files
 * up one by one and then starts the backend job that publishes them; the dialog can be closed once the
 * files are up and the batch list shows the rest.
 */
export function UploadProductImagesDialog({ open, onOpenChange }: UploadProductImagesDialogProps) {
  const { t } = useTranslation("back-office");
  const queryClient = useQueryClient();

  const [step, setStep] = useState<WizardStep>("scope");
  const [chainName, setChainName] = useState("");
  const [referenceStoreName, setReferenceStoreName] = useState("");
  const [countryPrefix, setCountryPrefix] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [precheck, setPrecheck] = useState<ProductImagePrecheck | null>(null);
  const [previewFilter, setPreviewFilter] = useState<PreviewFilter>("all");
  const [uploads, setUploads] = useState<UploadCounts>(NO_UPLOADS);
  const [uploading, setUploading] = useState(false);
  // Set when the batch could not be created or its publishing could not be started
  const [publishFailed, setPublishFailed] = useState(false);

  const { data: options, isFetching: optionsFetching } = useProductImagesOptionsQuery({ enabled: open });
  const precheckFiles = usePrecheckProductImagesMutation();
  const createBatch = useCreateProductImageBatchMutation();
  const uploadFile = useUploadProductImageFileMutation();
  const publishBatch = usePublishProductImageBatchMutation();

  const selectedChain = useMemo(
    () => options?.chains.find((chain) => chain.chainName === chainName) ?? null,
    [options, chainName],
  );
  const usesCountryPrefix = selectedChain?.filenameId === "item_number";

  const STEPS = useMemo<{ key: WizardStep; label: string }[]>(
    () => [
      { key: "scope", label: t("productImages.wizard.steps.scope") },
      { key: "files", label: t("productImages.wizard.steps.files") },
      { key: "preview", label: t("productImages.wizard.steps.preview") },
      { key: "publish", label: t("productImages.wizard.steps.publish") },
    ],
    [t],
  );
  const currentStepIndex = STEPS.findIndex((s) => s.key === step);

  useEffect(
    function resetWhenClosed() {
      if (open) return;
      setStep("scope");
      setChainName("");
      setReferenceStoreName("");
      setCountryPrefix("");
      setFiles([]);
      setPrecheck(null);
      setPreviewFilter("all");
      setThumbnails(new Map());
      setEnlargedFile(null);
      setUploads(NO_UPLOADS);
      setUploading(false);
      setPublishFailed(false);
    },
    [open],
  );

  const scope = {
    chainName,
    referenceStoreName,
    countryPrefix: usesCountryPrefix ? countryPrefix.trim() : undefined,
  };

  async function runPrecheck(nextFiles: File[]) {
    setPrecheck(null);
    if (nextFiles.length === 0) return;
    try {
      setPrecheck(await precheckFiles.mutateAsync({ ...scope, fileNames: nextFiles.map((file) => file.name) }));
      setStep("preview");
    } catch {
      // Error toast handled by API middleware
    }
  }

  function handleFilesSelected(added: File[]) {
    const merged = mergeSelectedFiles(files, added);
    setFiles(merged);
    void runPrecheck(merged);
  }

  async function handlePublish() {
    if (!precheck) return;
    setStep("publish");
    setUploading(true);
    setPublishFailed(false);
    try {
      const batch = await createBatch.mutateAsync({ ...scope, fileNames: files.map((file) => file.name) });
      const targets = mappedFileNames(precheck);
      await runWithConcurrency(
        files.filter((file) => targets.has(file.name)),
        UPLOAD_CONCURRENCY,
        async (file) => {
          await uploadFile.mutateAsync({ importRequestId: batch.importRequestId, file });
          setUploads((prev) => ({ ...prev, done: prev.done + 1 }));
        },
        () => setUploads((prev) => ({ ...prev, done: prev.done + 1, errors: prev.errors + 1 })),
      );
      await publishBatch.mutateAsync(batch.importRequestId);
      toast.success(t("productImages.wizard.publish.doneToast"));
    } catch {
      // Error toast handled by API middleware
      setPublishFailed(true);
    } finally {
      setUploading(false);
      queryClient.invalidateQueries({ queryKey: ["product-images", "batches"] });
    }
  }

  const previewRows = useMemo(() => {
    const rows = precheck?.files ?? [];
    const filtered = previewFilter === "all" ? rows : rows.filter((row) => row.validation === previewFilter);
    return [...filtered].sort(
      (a, b) => PREVIEW_ORDER[a.validation] - PREVIEW_ORDER[b.validation] || a.fileName.localeCompare(b.fileName),
    );
  }, [precheck, previewFilter]);

  // Thumbnails of the rows on screen, decoded from the picked files while the preview is open.
  const [thumbnails, setThumbnails] = useState<Map<string, string>>(new Map());
  useEffect(
    function decodeThumbnails() {
      if (step !== "preview") return;
      let cancelled = false;
      const fileByName = new Map(files.map((file) => [file.name, file]));
      (async () => {
        for (const row of previewRows.slice(0, PREVIEW_ROWS_LIMIT)) {
          const file = fileByName.get(row.fileName);
          if (!file || cancelled) continue;
          const url = await makeThumbnail(file);
          if (!url || cancelled) continue;
          setThumbnails((prev) => new Map(prev).set(row.fileName, url));
        }
      })();
      return () => {
        cancelled = true;
      };
    },
    [step, previewRows, files],
  );

  // Full-size view of one picked file, opened from its thumbnail
  const [enlargedFile, setEnlargedFile] = useState<File | null>(null);
  const [enlargedUrl, setEnlargedUrl] = useState<string | null>(null);
  useEffect(
    function loadEnlargedImage() {
      if (!enlargedFile) {
        setEnlargedUrl(null);
        return;
      }
      const url = URL.createObjectURL(enlargedFile);
      setEnlargedUrl(url);
      return () => URL.revokeObjectURL(url);
    },
    [enlargedFile],
  );

  const mappedCount = precheck?.summary.mapped ?? 0;
  const uploadTotal = mappedCount;
  const isBusy = precheckFiles.isPending || createBatch.isPending || publishBatch.isPending || uploading;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && isBusy) return;
        onOpenChange(o);
      }}
    >
      <DialogContent
        id="dialog-upload-product-images"
        className="sm:w-fit sm:min-w-[42rem] sm:max-w-[60rem] p-0 gap-0 overflow-hidden flex flex-col"
        aria-describedby={undefined}
        showCloseButton={!isBusy}
      >
        <div className="flex flex-row max-h-[calc(85vh-3.5rem)]">
          <WizardPane icon={Images} />
          <div className="flex-1 px-6 pt-6 flex flex-col gap-4 min-w-0 min-h-0">
            <DialogHeader>
              <DialogTitle>{t("productImages.wizard.title")}</DialogTitle>
              {step !== "scope" && selectedChain && (
                <div className="flex items-center gap-2 pt-1">
                  <KeyChip label={selectedChain.chainName} variant="chain" />
                  <KeyChip label={referenceStoreName} variant="store" />
                </div>
              )}
            </DialogHeader>

            <div className="inline-flex self-start text-xs">
              {STEPS.map((s, i) => {
                const isPast = i < currentStepIndex;
                const isCurrent = i === currentStepIndex;
                const isAfterCurrent = i === currentStepIndex + 1;
                const isFirst = i === 0;
                const isLast = i === STEPS.length - 1;
                return (
                  <div
                    key={s.key}
                    className={cn(
                      "relative flex items-center justify-center gap-1.5 border-y border-l pl-2 pr-3 py-1.5",
                      isLast && "border-r",
                      isFirst && "rounded-l-md",
                      isLast && "rounded-r-md",
                      isCurrent && "z-10 bg-primary font-medium text-primary-foreground border-primary-edge",
                      isAfterCurrent && "border-l-primary-edge",
                      isPast && "bg-muted text-muted-foreground",
                      !isPast && !isCurrent && "text-muted-foreground",
                    )}
                  >
                    <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-[10px] font-medium tabular-nums">
                      {isPast ? <Check className="h-3.5 w-3.5" /> : i + 1}
                    </span>
                    <span>{s.label}</span>
                  </div>
                );
              })}
            </div>

            <div className="-mx-6 flex-1 min-h-0 overflow-y-auto px-6 pt-1 pb-6 space-y-4">
              {step === "scope" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>{t("productImages.wizard.scope.chain")}</Label>
                      <VirtualSelectPopover
                        value={chainName}
                        onChange={(value) => {
                          const chain = options?.chains.find((c) => c.chainName === value);
                          setChainName(value);
                          // The chain's defaults; both stay editable.
                          setReferenceStoreName(chain?.referenceStoreName ?? "");
                          setCountryPrefix(chain?.countryPrefix ?? "");
                        }}
                        options={(options?.chains ?? []).map((c) => ({ label: c.legibleName, value: c.chainName }))}
                        placeholder={t("productImages.wizard.scope.selectChain")}
                        searchPlaceholder={t("imports.wizard.scope.searchChains")}
                        emptyText={t("imports.wizard.scope.noChains")}
                        loading={optionsFetching && !options}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t("productImages.wizard.scope.referenceStore")}</Label>
                      <VirtualSelectPopover
                        value={referenceStoreName}
                        onChange={setReferenceStoreName}
                        options={(selectedChain?.stores ?? []).map((s) => ({
                          label: s.legibleName,
                          value: s.storeName,
                        }))}
                        placeholder={t("productImages.wizard.scope.selectStore")}
                        searchPlaceholder={t("imports.wizard.scope.searchStores")}
                        emptyText={t("imports.wizard.scope.noStores")}
                        disabled={!selectedChain}
                      />
                      <p className="text-xs text-muted-foreground">
                        {t("productImages.wizard.scope.referenceStoreHint")}
                      </p>
                    </div>
                  </div>
                  {selectedChain && (
                    <p className="text-sm text-muted-foreground">
                      {t("productImages.wizard.scope.filenameRule")}{" "}
                      <span className="text-foreground">
                        {usesCountryPrefix
                          ? t("productImages.wizard.scope.byItemNumber", { length: selectedChain.itemNumberLength })
                          : t("productImages.wizard.scope.byBarcode")}
                      </span>
                    </p>
                  )}
                  {usesCountryPrefix && (
                    <div className="w-40 space-y-1.5">
                      <Label htmlFor="product-images-country-prefix">
                        {t("productImages.wizard.files.countryPrefix")}
                      </Label>
                      <Input
                        id="product-images-country-prefix"
                        value={countryPrefix}
                        onChange={(e) => setCountryPrefix(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        {t("productImages.wizard.files.countryPrefixHint")}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {step === "files" && (
                <div className="space-y-4">
                  <ImageFilesPicker
                    onSelect={handleFilesSelected}
                    disabled={isBusy}
                    title={t("productImages.wizard.files.dropTitle")}
                    subtitle={t("productImages.wizard.files.dropSubtitle", {
                      extensions: ACCEPTED_IMAGE_ACCEPT.replaceAll(",", ", "),
                    })}
                  />
                  {precheckFiles.isPending && <InlineSpinner label={t("productImages.wizard.files.checking")} />}
                  <p className="text-xs text-muted-foreground">{t("productImages.wizard.files.hint")}</p>
                </div>
              )}

              {step === "preview" && precheck && (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {PREVIEW_FILTERS.map((filter) => (
                      <FilterChip
                        key={filter}
                        label={
                          filter === "all"
                            ? t("productImages.wizard.preview.all")
                            : t(`productImages.validation.${filter}`)
                        }
                        value={
                          filter === "all"
                            ? precheck.summary.total
                            : precheck.files.filter((row) => row.validation === filter).length
                        }
                        tone={FILTER_TONES[filter]}
                        selected={previewFilter === filter}
                        onClick={() => setPreviewFilter(filter)}
                      />
                    ))}
                  </div>
                  {previewRows.length > PREVIEW_ROWS_LIMIT && (
                    <p className="text-xs text-muted-foreground">
                      {t("productImages.wizard.preview.showing", {
                        shown: PREVIEW_ROWS_LIMIT,
                        total: previewRows.length,
                      })}
                    </p>
                  )}
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-14" />
                          <TableHead>{t("productImages.columns.fileName")}</TableHead>
                          <TableHead>{t("productImages.columns.validation")}</TableHead>
                          <TableHead>
                            {usesCountryPrefix ? t("productImages.columns.itemNumber") : t("productImages.columns.upc")}
                          </TableHead>
                          <TableHead>{t("productImages.columns.product")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewRows.slice(0, PREVIEW_ROWS_LIMIT).map((row) => (
                          <TableRow key={row.fileName}>
                            <TableCell className="w-14 py-1">
                              {thumbnails.get(row.fileName) ? (
                                <button
                                  type="button"
                                  className="cursor-zoom-in"
                                  title={t("productImages.wizard.preview.enlarge")}
                                  onClick={() =>
                                    setEnlargedFile(files.find((file) => file.name === row.fileName) ?? null)
                                  }
                                >
                                  <img
                                    src={thumbnails.get(row.fileName)}
                                    alt=""
                                    className="h-10 w-10 max-w-none rounded border bg-white object-contain"
                                  />
                                </button>
                              ) : (
                                <div className="h-10 w-10 rounded border bg-muted" />
                              )}
                            </TableCell>
                            <TableCell className="font-medium">{row.fileName}</TableCell>
                            <TableCell>
                              <ProductImageValidationBadge validation={row.validation} />
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {(usesCountryPrefix ? row.itemNumber : row.upc) ?? "—"}
                            </TableCell>
                            <TableCell className="whitespace-normal">
                              {row.productName ?? (row.detail ? null : "—")}
                              {row.detail && (
                                <p className={cn("text-muted-foreground", row.productName && "text-xs")}>
                                  {row.detail}
                                </p>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <p className="text-sm">
                    {t("productImages.wizard.preview.publishSummary", {
                      mapped: mappedCount,
                      total: precheck.summary.total,
                    })}
                  </p>
                </div>
              )}

              {step === "publish" && (
                <div className="space-y-4">
                  {createBatch.isPending ? (
                    <InlineSpinner label={t("productImages.wizard.publish.creating")} />
                  ) : publishFailed ? (
                    <div className="flex items-center gap-2 rounded-lg border border-destructive bg-destructive/10 p-4 text-sm">
                      <TriangleAlert className="h-4 w-4 shrink-0 text-destructive" />
                      <span className="font-medium text-destructive">
                        {t("productImages.wizard.publish.failedTitle")}
                      </span>
                    </div>
                  ) : uploading && uploads.done < uploadTotal ? (
                    <div className="flex flex-col items-center gap-2 py-8">
                      <InlineSpinner
                        label={t("productImages.wizard.publish.uploading", { done: uploads.done, total: uploadTotal })}
                        className="py-0"
                      />
                      <ProgressBar value={uploadTotal ? uploads.done / uploadTotal : 0} className="max-w-[16rem]" />
                    </div>
                  ) : uploading ? (
                    <InlineSpinner label={t("productImages.wizard.publish.starting")} />
                  ) : (
                    <div className="flex items-center gap-2 rounded-md border p-3 text-sm">
                      {uploads.errors > 0 ? (
                        <TriangleAlert className="h-4 w-4 shrink-0 text-amber-600" />
                      ) : (
                        <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />
                      )}
                      <span>{t("productImages.wizard.publish.doneTitle")}</span>
                    </div>
                  )}
                  {!publishFailed && (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <span className="text-muted-foreground">{t("productImages.wizard.publish.uploaded")}</span>
                      <span className="tabular-nums">{uploads.done - uploads.errors}</span>
                      <span className="text-muted-foreground">{t("productImages.wizard.publish.uploadErrors")}</span>
                      <span className="tabular-nums">{uploads.errors}</span>
                    </div>
                  )}
                  {!uploading && uploads.errors > 0 && (
                    <p className="text-xs text-muted-foreground">{t("productImages.wizard.publish.doneHint")}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          {step !== "publish" && (
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isBusy}>
              {t("buttons.cancel")}
            </Button>
          )}

          {step === "scope" && (
            <Button
              onClick={() => (files.length > 0 ? runPrecheck(files) : setStep("files"))}
              disabled={!selectedChain || !referenceStoreName || isBusy}
            >
              {t("buttons.next")}
            </Button>
          )}

          {step === "files" && (
            <Button variant="outline" onClick={() => setStep("scope")} disabled={isBusy}>
              {t("buttons.back")}
            </Button>
          )}

          {step === "preview" && (
            <>
              <Button variant="outline" onClick={() => setStep("files")} disabled={isBusy}>
                {t("buttons.back")}
              </Button>
              <Button onClick={handlePublish} disabled={mappedCount === 0 || isBusy}>
                <Images className="h-4 w-4" />
                {t("productImages.wizard.preview.publishButton", { count: mappedCount })}
              </Button>
            </>
          )}

          {step === "publish" && publishFailed && (
            <Button variant="outline" onClick={() => setStep("preview")}>
              {t("buttons.back")}
            </Button>
          )}
          {step === "publish" && (
            <Button onClick={() => onOpenChange(false)} disabled={isBusy}>
              {t("buttons.done")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      <Dialog open={enlargedFile != null} onOpenChange={(o) => !o && setEnlargedFile(null)}>
        <DialogContent
          id="dialog-product-image-enlarged"
          className="sm:max-w-[48rem]"
          aria-describedby={undefined}
          // Unlike the forms behind the shared dialog, a picture can close on a click anywhere outside it
          onInteractOutside={() => setEnlargedFile(null)}
        >
          <DialogHeader>
            <DialogTitle>{enlargedFile?.name}</DialogTitle>
          </DialogHeader>
          {enlargedUrl && (
            <img src={enlargedUrl} alt="" className="mx-auto max-h-[70vh] w-auto max-w-full object-contain" />
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
